"use client";

import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@akashnetwork/ui/components";
import { cn } from "@akashnetwork/ui/utils";
import { Check, ChevronsUpDown, Plus, Users } from "lucide-react";
import { TOUR_TARGET } from "@src/components/whats-new/tourTargets";
import { useActiveOrganization } from "@src/context/OrganizationProvider/OrganizationProvider";
import { useWallet } from "@src/context/WalletProvider";
import type { OrganizationOutput } from "@src/types/organization";
import { ORGANIZATION_ROLE_LABELS } from "../organizationAccess";
import { OrganizationAvatar } from "../OrganizationAvatar/OrganizationAvatar";
export const DEPENDENCIES = {
  useActiveOrganization,
  useWallet
};
type Props = {
  dependencies?: typeof DEPENDENCIES;
  /** "header" keeps the trigger compact on phones; "sheet" fills the width of the mobile navigation sheet. */
  layout?: "header" | "sheet";
  /** Adds the "Create organization" row and shows the switcher without a team; headers that cannot host the dialog leave it out. */
  onCreateOrganization?: () => void;
  /** Called after a selection so a surrounding sheet can close. */
  onNavigate?: () => void;
};
const TRIGGER_CLASSES = "inline-flex h-9 shrink-0 items-center gap-1 rounded-[9px] border border-border bg-background px-1.5 text-[13.5px] font-semibold text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:bg-muted sm:gap-2 sm:pl-1.5 sm:pr-2.5";
const SECTION_LABEL_CLASSES = "px-[9px] pb-[5px] pt-2 font-mono text-[10.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground";
const ROW_CLASSES = "min-h-9 cursor-pointer gap-2.5 rounded-[7px] px-[9px] py-0 text-[13.5px] font-medium focus:bg-muted focus:text-foreground";
const BADGE_CLASSES = "inline-flex shrink-0 rounded-md border px-1.5 py-px text-[11px] font-medium leading-4";
const TRIAL_BADGE_CLASSES = "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400";
const PERSONAL_BADGE_CLASSES = "border-border bg-muted text-muted-foreground";
export function OrganizationSwitcher({
  dependencies: d = DEPENDENCIES,
  layout = "header",
  onCreateOrganization,
  onNavigate
}: Props) {
  const {
    organizations,
    activeOrganization,
    switchOrganization
  } = d.useActiveOrganization();
  const {
    isTrialing
  } = d.useWallet();
  const personalOrganization = organizations.find(organization => organization.type === "personal");
  const teamOrganizations = organizations.filter(organization => organization.type === "team");
  const hasTeams = teamOrganizations.length > 0;
  const idsOfTeamsSharingAName = idsOfOrganizationsSharingAName(teamOrganizations);
  if (!activeOrganization || !hasTeams && !onCreateOrganization) return null;
  const isPersonalAccountTrialing = isTrialingInPersonalAccount(activeOrganization, isTrialing);
  const select = (organization: OrganizationOutput) => {
    if (organization.id !== activeOrganization.id) switchOrganization(organization.id);
    onNavigate?.();
  };
  const renderRow = (organization: OrganizationOutput) => {
    const isCurrent = organization.id === activeOrganization.id;
    return <DropdownMenuItem key={organization.id} role="menuitemradio" aria-checked={isCurrent} className={ROW_CLASSES} onSelect={() => select(organization)} data-sentry-element="DropdownMenuItem" data-sentry-component="renderRow" data-sentry-source-file="OrganizationSwitcher.tsx">
        <OrganizationAvatar organization={organization} size={20} data-sentry-element="OrganizationAvatar" data-sentry-source-file="OrganizationSwitcher.tsx" />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate">{organization.name}</span>
          {idsOfTeamsSharingAName.has(organization.id) && <span className="truncate text-xs font-normal text-muted-foreground">{organization.slug}</span>}
        </span>
        {organization.type === "team" ? <span className="shrink-0 text-xs font-normal text-muted-foreground">{ORGANIZATION_ROLE_LABELS[organization.role]}</span> : <PersonalAccountBadge isTrialing={isPersonalAccountTrialing} />}
        <span className="inline-flex w-3.5 shrink-0 justify-center">{isCurrent && <Check className="h-3.5 w-3.5" />}</span>
      </DropdownMenuItem>;
  };
  return <DropdownMenu modal={false} data-sentry-element="DropdownMenu" data-sentry-component="OrganizationSwitcher" data-sentry-source-file="OrganizationSwitcher.tsx">
      <DropdownMenuTrigger aria-label={`${activeOrganization.name}, switch organization`} data-tour-target={TOUR_TARGET.organizationSwitcher} className={cn(TRIGGER_CLASSES, layout === "sheet" && "w-full")} data-sentry-element="DropdownMenuTrigger" data-sentry-source-file="OrganizationSwitcher.tsx">
        <OrganizationAvatar organization={activeOrganization} size={22} data-sentry-element="OrganizationAvatar" data-sentry-source-file="OrganizationSwitcher.tsx" />
        <span className={cn("truncate", layout === "header" ? "hidden max-w-[180px] sm:block" : "min-w-0 flex-1 text-left")}>{activeOrganization.name}</span>
        {activeOrganization.type === "personal" && <PersonalAccountBadge isTrialing={isPersonalAccountTrialing} className={cn(layout === "header" && "hidden sm:inline-flex")} />}
        <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" data-sentry-element="ChevronsUpDown" data-sentry-source-file="OrganizationSwitcher.tsx" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align={layout === "sheet" ? "start" : "end"} sideOffset={6} collisionPadding={8} className="organization-switcher-menu w-[292px] max-w-[calc(100vw-1rem)] rounded-[11px] p-[5px] shadow-[0_16px_38px_-12px_rgba(0,0,0,0.28)]" data-sentry-element="DropdownMenuContent" data-sentry-source-file="OrganizationSwitcher.tsx">
        {personalOrganization && renderRow(personalOrganization)}
        {hasTeams ? <>
            <DropdownMenuLabel className={SECTION_LABEL_CLASSES}>Organizations</DropdownMenuLabel>
            {teamOrganizations.map(renderRow)}
          </> : <NoTeamsPrompt />}
        {onCreateOrganization && <>
            <DropdownMenuSeparator className="my-[5px] bg-border" />
            <DropdownMenuItem className={ROW_CLASSES} data-tour-target={TOUR_TARGET.createOrganization} onSelect={() => {
          onCreateOrganization();
          onNavigate?.();
        }}>
              <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center">
                <Plus className="h-[15px] w-[15px] text-muted-foreground" />
              </span>
              <span className="min-w-0 flex-1 truncate">Create organization</span>
            </DropdownMenuItem>
          </>}
      </DropdownMenuContent>
    </DropdownMenu>;
}

/** The wallet follows the active organization, so its trial only describes the personal account while that account is active. */
function isTrialingInPersonalAccount(activeOrganization: OrganizationOutput, isActiveWalletTrialing: boolean): boolean {
  return activeOrganization.type === "personal" && isActiveWalletTrialing;
}
function PersonalAccountBadge({
  isTrialing,
  className
}: {
  isTrialing: boolean;
  className?: string;
}) {
  return <span className={cn(BADGE_CLASSES, isTrialing ? TRIAL_BADGE_CLASSES : PERSONAL_BADGE_CLASSES, className)} data-sentry-component="PersonalAccountBadge" data-sentry-source-file="OrganizationSwitcher.tsx">{isTrialing ? "Trial" : "Personal"}</span>;
}
function NoTeamsPrompt() {
  return <div className="flex flex-col items-center px-4 pb-4 pt-5 text-center" data-sentry-component="NoTeamsPrompt" data-sentry-source-file="OrganizationSwitcher.tsx">
      <span className="mb-3 flex h-8 w-8 items-center justify-center rounded-[8px] border border-border bg-background">
        <Users className="h-4 w-4 text-muted-foreground" data-sentry-element="Users" data-sentry-source-file="OrganizationSwitcher.tsx" />
      </span>
      <p className="text-[13.5px] font-semibold text-foreground">Deploy as a team</p>
      <p className="mt-0.5 text-pretty text-[12.5px] leading-[18px] text-muted-foreground">Share projects and billing with teammates.</p>
    </div>;
}

/** Teams a person joins may share a name, ignoring case, with one they already belong to. */
function idsOfOrganizationsSharingAName(organizations: OrganizationOutput[]): Set<string> {
  const namesSeen = new Set<string>();
  const sharedNames = new Set<string>();
  for (const {
    name
  } of organizations) {
    const comparableName = name.toLowerCase();
    if (namesSeen.has(comparableName)) sharedNames.add(comparableName);
    namesSeen.add(comparableName);
  }
  return new Set(organizations.filter(({
    name
  }) => sharedNames.has(name.toLowerCase())).map(({
    id
  }) => id));
}